import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Send, X } from "lucide-react";
import { api } from "../../lib/api";
import { BTN_PRIMARY, BTN_QUIET, errorDetail } from "./ContractCard";

/**
 * "Trimite contractele participanților deja plătiți": the preview first —
 * who is ready, who is blocked and why, the credits it costs, the groups
 * about to start — then, on confirmation, up to 30 contracts per press,
 * 08:00–21:00 only. What goes joins the usual reminders.
 */
export default function ContractBatchDialog({ onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      setPreview((await api.get("/admin/contracts/batch/preview")).data);
    } catch (err) {
      setPreview({ error: errorDetail(err, "Previzualizarea nu a putut fi încărcată.") });
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !sending) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, sending]);

  const send = async () => {
    setSending(true);
    try {
      const r = (await api.post("/admin/contracts/batch/send")).data;
      setResult(r);
      toast.success(`Am trimis ${r.sent} ${r.sent === 1 ? "contract" : "contracte"}.`);
      onSent?.();
      await load();
    } catch (err) {
      toast.error(errorDetail(err, "Trimiterea în lot nu a reușit."));
    } finally {
      setSending(false);
    }
  };

  const p = preview;
  const now = Math.min(p?.ready || 0, p?.per_run || 30);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={() => !sending && onClose()} data-testid="contract-batch-dialog">
      <div role="dialog" aria-modal="true" aria-label="Trimite contractele participanților deja plătiți"
        onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-[#373A45]">Trimite contractele participanților deja plătiți</h2>
          <button type="button" onClick={onClose} disabled={sending} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide">
            <X size={18} />
          </button>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-[#6B7280]">
          Participanții plătiți care nu au primit încă niciun contract. Cei blocați nu primesc nimic.
          Contractele trimise primesc apoi reamintirile obișnuite.
        </p>

        {!p && <div className="mt-4 flex items-center gap-2 text-sm text-[#6B7280]"><Loader2 size={14} className="animate-spin" /> Se calculează…</div>}
        {p?.error && <p className="mt-4 text-sm text-[#9b1f2c]">{p.error}</p>}

        {p && !p.error && (
          <>
            <dl className="mt-4 grid grid-cols-2 gap-2 text-sm" data-testid="contract-batch-preview">
              <div className="rounded-xl bg-[#EAF4EE] px-3 py-2">
                <dt className="text-xs text-[#2F6B4E]">Gata de trimis</dt>
                <dd className="text-2xl font-semibold tabular-nums text-[#2F6B4E]">{p.ready}</dd>
              </div>
              <div className="rounded-xl bg-[#F3F4F6] px-3 py-2">
                <dt className="text-xs text-[#6B7280]">Blocați</dt>
                <dd className="text-2xl font-semibold tabular-nums text-[#373A45]">{p.blocked}</dd>
              </div>
              <div className="rounded-xl bg-[#F3F4F6] px-3 py-2">
                <dt className="text-xs text-[#6B7280]">Credite eSemneaza</dt>
                <dd className="text-2xl font-semibold tabular-nums text-[#373A45]">{p.credits}</dd>
              </div>
              <div className={`rounded-xl px-3 py-2 ${p.groups_starting_soon ? "bg-[#FDF3E7]" : "bg-[#F3F4F6]"}`}>
                <dt className="text-xs text-[#6B7280]">Grupe care încep în sub 72 h</dt>
                <dd className="text-2xl font-semibold tabular-nums text-[#373A45]">
                  {p.groups_starting_soon}
                  {p.participants_starting_soon > 0 && (
                    <span className="ml-1 text-xs font-normal text-[#6B7280]">({p.participants_starting_soon} participanți)</span>
                  )}
                </dd>
              </div>
            </dl>

            {p.blocked_reasons?.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">De ce sunt blocați</div>
                <ul className="mt-1 space-y-0.5 text-xs text-[#373A45]">
                  {p.blocked_reasons.map((r) => (
                    <li key={r.code} className="flex gap-2"><span className="tabular-nums font-semibold">{r.count}</span> {r.message}</li>
                  ))}
                </ul>
              </div>
            )}

            {p.ready > p.per_run && (
              <p className="mt-3 text-xs text-[#6B7280]">
                Pleacă {p.per_run} la o apăsare, cei cu grupa cea mai apropiată primii. Apasă din nou pentru următorii.
              </p>
            )}
            {!p.window_open && (
              <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-[#FDF3E7] px-3 py-2 text-xs text-[#8a6a1a]">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                Trimiterea în lot pleacă doar între 08:00 și 21:00, ora României.
              </p>
            )}
            {result && (
              <p className="mt-3 text-xs text-[#373A45]" data-testid="contract-batch-result">
                Trimise: <strong>{result.sent}</strong>
                {result.failed > 0 && <> · eșuate: <strong className="text-[#9b1f2c]">{result.failed}</strong></>}
                {result.stopped && <> · oprit: {result.stopped}</>}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={onClose} disabled={sending} className={BTN_QUIET}>Închide</button>
              <button type="button" onClick={send} disabled={sending || !p.ready || !p.window_open}
                className={BTN_PRIMARY} data-testid="contract-batch-send">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Trimite {now} {now === 1 ? "contract" : "contracte"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
